/*
 * Studio Park Holiday Calendar (StudioPark_HolidayCalendar widget) - front end.
 *
 * The Razor view emits a sanitized JSON config in <script type="application/json" class="hc-config">;
 * this file renders the month grids, the list view, and the date-detail dialog from it.
 *
 * Time zone: the calendar is locked to Eastern Time. Every date is handled as a plain
 * "YYYY-MM-DD" string and weekday math uses UTC-anchored dates, so the grid never shifts for a
 * visitor in another zone. "Today" / past-date dimming is computed in America/New_York, event
 * times are shown as entered with an "ET" suffix, and calendar invites carry
 * TZID=America/New_York so the visitor's own calendar app converts them correctly.
 *
 * QA helper: ?hcToday=2026-11-20 overrides "today".
 */
(function () {
    'use strict';

    var TZ = 'America/New_York';
    var TZ_LABEL = 'ET';
    var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    var MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Label (tile text) colors the editor can pick. "auto" = white on dark tiles, dark green on light tiles.
    var LABEL_COLORS = {
        white: '#FFFFFF',
        yellow: '#F5E663',
        cream: '#F2ECD9',
        pink: '#E79AC2',
        red: '#D8232A',
        darkgreen: '#15633F',
        black: '#212126'
    };
    var TILES = { green: 1, darkgreen: 1, red: 1, lightgreen: 1, cream: 1 };
    var LIGHT_TILES = { lightgreen: 1, cream: 1 };

    var ICONS = {
        star: '<svg viewBox="0 0 40 40" aria-hidden="true" focusable="false"><path d="M20 1.5l2.6 12.2L33 6.6l-7.1 10.4 12.6 1.4-11.8 4.6 9.2 8.7-12.1-3.6 1.2 12.4L20 29.6l-5 11.3 1.2-12.4-12.1 3.6 9.2-8.7L1.5 18.4l12.6-1.4L7 6.6l10.4 7.1z"/></svg>',
        close: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" fill="none"/></svg>',
        prev: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>',
        next: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>',
        clock: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" fill="none"/><path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>',
        cal: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" stroke-width="2" fill="none"/><path d="M3.5 10h17M8 3v4M16 3v4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
        list: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 6h12M8 12h12M8 18h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="4" cy="6" r="1.4" fill="currentColor"/><circle cx="4" cy="12" r="1.4" fill="currentColor"/><circle cx="4" cy="18" r="1.4" fill="currentColor"/></svg>',
        link: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>',
        arrow: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>'
    };

    // ------------------------------------------------------------------ date + time helpers

    function pad(n) { return (n < 10 ? '0' : '') + n; }
    function iso(y, m, d) { return y + '-' + pad(m) + '-' + pad(d); }           // m is 1-based
    function parseIso(s) { var p = s.split('-'); return { y: +p[0], m: +p[1], d: +p[2] }; }
    function utc(s) { var p = parseIso(s); return new Date(Date.UTC(p.y, p.m - 1, p.d)); }
    function dowOf(s) { return utc(s).getUTCDay(); }
    function daysIn(y, m) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }
    function addDays(s, n) {
        var dt = utc(s);
        dt.setUTCDate(dt.getUTCDate() + n);
        return iso(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
    }

    function todayET() {
        var q = /[?&]hcToday=(\d{4}-\d{2}-\d{2})/.exec(window.location.search);
        if (q) { return q[1]; }
        try {
            var o = {};
            new Intl.DateTimeFormat('en-US', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
                .formatToParts(new Date())
                .forEach(function (p) { o[p.type] = p.value; });
            return o.year + '-' + o.month + '-' + o.day;
        } catch (e) {
            var d = new Date();
            return iso(d.getFullYear(), d.getMonth() + 1, d.getDate());
        }
    }

    function parseTime(t) {
        var m = /^(\d{2}):(\d{2})$/.exec(t || '');
        return m ? { h: +m[1], min: +m[2] } : null;
    }
    function fmtTime(t, withMeridiem) {
        var s = (t.h % 12 || 12) + (t.min ? ':' + pad(t.min) : '');
        return withMeridiem ? s + ' ' + (t.h < 12 ? 'AM' : 'PM') : s;
    }
    // "6–8 PM", "11 AM – 2 PM", "6:30 PM"
    function fmtRange(start, end) {
        var s = parseTime(start), e = parseTime(end);
        if (!s) { return ''; }
        if (!e) { return fmtTime(s, true); }
        if ((s.h < 12) === (e.h < 12)) { return fmtTime(s, false) + '\u2013' + fmtTime(e, true); }
        return fmtTime(s, true) + ' \u2013 ' + fmtTime(e, true);
    }

    function longDate(s) { var p = parseIso(s); return MONTHS[p.m - 1] + ' ' + p.d; }
    function shortDate(s) { var p = parseIso(s); return DOW_SHORT[dowOf(s)] + ', ' + MONTHS_SHORT[p.m - 1] + ' ' + p.d; }

    // ------------------------------------------------------------------ DOM helpers

    function el(tag, cls, text) {
        var n = document.createElement(tag);
        if (cls) { n.className = cls; }
        if (text !== undefined && text !== null) { n.textContent = text; }
        return n;
    }
    function icon(name, cls) {
        var s = el('span', 'hc-ico ' + (cls || ''));
        s.innerHTML = ICONS[name];   // static markup only
        return s;
    }
    function trim(s) { return (s || '').replace(/^\s+|\s+$/g, ''); }

    // ------------------------------------------------------------------ day helpers

    function events(day) { return (day && day.events) || []; }
    function isListWorthy(day) {
        return !!(day && (day.important || trim(day.label) || trim(day.note) || events(day).length));
    }
    function hasContent(day) {
        return !!(day && (day.closed || day.market || isListWorthy(day)));
    }
    function tileText(day) {
        if (trim(day.label)) { return trim(day.label); }
        return events(day).map(function (e) { return trim(e.title); }).filter(Boolean).join(' & ');
    }
    function labelColor(day) {
        if (day.labelColor === 'custom' && /^#[0-9a-f]{6}$/i.test(day.customColor || '')) { return day.customColor; }
        return LABEL_COLORS[day.labelColor] || '';
    }

    // ------------------------------------------------------------------ calendar invites

    function icsEscape(s) {
        return (s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
    }
    function icsFold(line) {
        var out = [];
        while (line.length > 73) { out.push(line.slice(0, 73)); line = ' ' + line.slice(73); }
        out.push(line);
        return out.join('\r\n');
    }
    function stamp(dateStr, t) { return dateStr.replace(/-/g, '') + 'T' + pad(t.h) + pad(t.min) + '00'; }

    // Start/end as local Eastern wall-clock stamps. Missing end = +1h; an end before the start rolls to the next day.
    function eventSpan(dateStr, ev) {
        var s = parseTime(ev.start);
        if (!s) { return null; }
        var e = parseTime(ev.end);
        var endDate = dateStr;
        if (!e) {
            e = { h: (s.h + 1) % 24, min: s.min };
            if (s.h === 23) { endDate = addDays(dateStr, 1); }
        } else if (e.h * 60 + e.min <= s.h * 60 + s.min) {
            endDate = addDays(dateStr, 1);
        }
        return { start: stamp(dateStr, s), end: stamp(endDate, e) };
    }

    function inviteDetails(ev, pageUrl) {
        var parts = [];
        if (trim(ev.subtitle)) { parts.push(trim(ev.subtitle)); }
        if (trim(ev.description)) { parts.push(trim(ev.description)); }
        if (ev.linkUrl) { parts.push(absUrl(ev.linkUrl)); }
        parts.push(pageUrl);
        return parts.join('\n\n');
    }

    function googleUrl(dateStr, ev, cfg, pageUrl) {
        var span = eventSpan(dateStr, ev);
        if (!span) { return ''; }
        return 'https://calendar.google.com/calendar/render?action=TEMPLATE' +
            '&text=' + encodeURIComponent(ev.title || 'Event') +
            '&dates=' + span.start + '/' + span.end +
            '&ctz=' + encodeURIComponent(TZ) +
            '&details=' + encodeURIComponent(inviteDetails(ev, pageUrl)) +
            (cfg.location ? '&location=' + encodeURIComponent(cfg.location) : '');
    }

    function buildIcs(dateStr, ev, idx, cfg, pageUrl) {
        var span = eventSpan(dateStr, ev);
        if (!span) { return ''; }
        var now = new Date();
        var dtstamp = now.getUTCFullYear() + pad(now.getUTCMonth() + 1) + pad(now.getUTCDate()) + 'T' +
            pad(now.getUTCHours()) + pad(now.getUTCMinutes()) + pad(now.getUTCSeconds()) + 'Z';
        var lines = [
            'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Studio Park//Holiday Calendar//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
            'BEGIN:VTIMEZONE', 'TZID:America/New_York',
            'BEGIN:DAYLIGHT', 'TZOFFSETFROM:-0500', 'TZOFFSETTO:-0400', 'TZNAME:EDT', 'DTSTART:19700308T020000', 'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU', 'END:DAYLIGHT',
            'BEGIN:STANDARD', 'TZOFFSETFROM:-0400', 'TZOFFSETTO:-0500', 'TZNAME:EST', 'DTSTART:19701101T020000', 'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU', 'END:STANDARD',
            'END:VTIMEZONE',
            'BEGIN:VEVENT',
            'UID:' + dateStr.replace(/-/g, '') + '-' + idx + '-' + slug(ev.title) + '@studiopark',
            'DTSTAMP:' + dtstamp,
            'DTSTART;TZID=America/New_York:' + span.start,
            'DTEND;TZID=America/New_York:' + span.end,
            'SUMMARY:' + icsEscape(ev.title || 'Event'),
            'DESCRIPTION:' + icsEscape(inviteDetails(ev, pageUrl)),
            'URL:' + pageUrl
        ];
        if (cfg.location) { lines.push('LOCATION:' + icsEscape(cfg.location)); }
        lines.push('END:VEVENT', 'END:VCALENDAR');
        return lines.map(icsFold).join('\r\n');
    }

    function slug(s) {
        return (s || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'event';
    }
    function absUrl(u) {
        try { return new URL(u, window.location.href).href; } catch (e) { return u; }
    }
    function downloadFile(name, text) {
        var blob = new Blob([text], { type: 'text/calendar;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        var a = el('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () { URL.revokeObjectURL(url); a.parentNode.removeChild(a); }, 500);
    }

    // ------------------------------------------------------------------ widget

    function HolidayCalendar(root) {
        var cfgNode = root.querySelector('.hc-config');
        this.root = root;
        this.app = root.querySelector('.hc-app');
        var rawCfg = cfgNode ? (cfgNode.getAttribute('data-config') || cfgNode.textContent) : '';
        try { this.cfg = JSON.parse(rawCfg || '{}') || {}; } catch (e) { this.cfg = {}; }
        this.cfg.labels = this.cfg.labels || {};
        this.days = {};
        var self = this;
        (this.cfg.days || []).forEach(function (d) { if (d && d.date) { self.days[d.date] = d; } });
        this.today = todayET();
        this.months = this.buildMonths();
        this.order = this.navOrder();        // dates the dialog can step through
        this.view = this.restoreView();
        this.showPast = false;
        this.render();
        this.openFromHash();
    }

    HolidayCalendar.prototype.label = function (key, fallback) {
        return trim(this.cfg.labels[key]) || fallback;
    };

    HolidayCalendar.prototype.buildMonths = function () {
        var m = /^(\d{4})-(\d{2})$/.exec(this.cfg.startMonth || '');
        var y = m ? +m[1] : new Date().getFullYear();
        var mo = m ? +m[2] : 11;
        var count = Math.max(1, Math.min(4, +this.cfg.monthCount || 2));
        var out = [];
        for (var i = 0; i < count; i++) {
            out.push({ y: y, m: mo });
            mo++;
            if (mo > 12) { mo = 1; y++; }
        }
        return out;
    };

    HolidayCalendar.prototype.inRange = function (dateStr) {
        var p = parseIso(dateStr);
        return this.months.some(function (mm) { return mm.y === p.y && mm.m === p.m; });
    };

    HolidayCalendar.prototype.navOrder = function () {
        var self = this;
        return Object.keys(this.days).filter(function (k) {
            return self.inRange(k) && hasContent(self.days[k]);
        }).sort();
    };

    HolidayCalendar.prototype.restoreView = function () {
        try {
            var v = window.localStorage.getItem('hc-view');
            if (v === 'list' || v === 'calendar') { return v; }
        } catch (e) { /* storage blocked - fine */ }
        return this.cfg.defaultView === 'list' ? 'list' : 'calendar';
    };

    HolidayCalendar.prototype.setView = function (v) {
        this.view = v;
        try { window.localStorage.setItem('hc-view', v); } catch (e) { /* ignore */ }
        this.app.setAttribute('data-view', v);
        var btns = this.app.querySelectorAll('.hc-view-btn');
        for (var i = 0; i < btns.length; i++) {
            btns[i].setAttribute('aria-pressed', btns[i].getAttribute('data-view') === v ? 'true' : 'false');
        }
        if (v === 'list') { this.renderList(); }
        else { this.fitLabels(); }
    };

    // Shrink tile text until its longest word fits the tile (and the text fits the line clamp).
    HolidayCalendar.prototype.fitLabels = function () {
        var labels = this.app.querySelectorAll('.hc-months .hc-label');
        for (var i = 0; i < labels.length; i++) {
            var l = labels[i];
            l.style.fontSize = '';
            l.classList.remove('is-squeezed');
            if (!l.offsetParent) { continue; }        // hidden (mobile / list view)
            var size = parseFloat(window.getComputedStyle(l).fontSize) || 11;
            var guard = 0;
            while (size > 7.5 && guard++ < 12 &&
                (l.scrollWidth > l.clientWidth + 1 || l.scrollHeight > l.clientHeight + 1)) {
                size -= 0.5;
                l.style.fontSize = size + 'px';
            }
            if (l.scrollWidth > l.clientWidth + 1) { l.classList.add('is-squeezed'); }
        }
    };

    // ---------------------------------------------------------------- render

    HolidayCalendar.prototype.render = function () {
        var self = this;
        var app = this.app;
        app.innerHTML = '';
        app.setAttribute('data-view', this.view);

        // Toolbar: legend + view toggle
        var bar = el('div', 'hc-toolbar');
        bar.appendChild(this.renderLegend());
        var toggle = el('div', 'hc-view-toggle');
        toggle.setAttribute('role', 'group');
        toggle.setAttribute('aria-label', 'Choose view');
        [['calendar', 'Calendar', 'cal'], ['list', 'List', 'list']].forEach(function (v) {
            var b = el('button', 'hc-view-btn');
            b.type = 'button';
            b.setAttribute('data-view', v[0]);
            b.setAttribute('aria-pressed', self.view === v[0] ? 'true' : 'false');
            b.appendChild(icon(v[2]));
            b.appendChild(el('span', null, v[1]));
            b.addEventListener('click', function () { self.setView(v[0]); });
            toggle.appendChild(b);
        });
        bar.appendChild(toggle);
        app.appendChild(bar);

        // Months
        var wrap = el('div', 'hc-months hc-count-' + this.months.length);
        this.months.forEach(function (mm) { wrap.appendChild(self.renderMonth(mm.y, mm.m)); });
        app.appendChild(wrap);

        // List
        this.listEl = el('div', 'hc-list');
        app.appendChild(this.listEl);
        if (this.view === 'list') { this.renderList(); }

        // Foot
        var foot = el('div', 'hc-foot');
        var tz = el('p', 'hc-tz');
        tz.appendChild(icon('clock'));
        tz.appendChild(el('span', null, 'All times are Eastern Time (' + TZ_LABEL + ').'));
        foot.appendChild(tz);
        if (trim(this.cfg.footnote)) { foot.appendChild(el('p', 'hc-footnote', trim(this.cfg.footnote))); }
        app.appendChild(foot);

        this.buildDialog();
        this.bindGridKeys(wrap);

        this.fitLabels();
        if (document.fonts && document.fonts.ready) { document.fonts.ready.then(function () { self.fitLabels(); }); }
        var t = null;
        window.addEventListener('resize', function () {
            clearTimeout(t);
            t = setTimeout(function () { self.fitLabels(); }, 150);
        });
    };

    HolidayCalendar.prototype.renderLegend = function () {
        var used = { market: false, important: false, closed: false };
        var self = this;
        Object.keys(this.days).forEach(function (k) {
            if (!self.inRange(k)) { return; }
            var d = self.days[k];
            if (d.market) { used.market = true; }
            if (d.important) { used.important = true; }
            if (d.closed) { used.closed = true; }
        });
        var ul = el('ul', 'hc-legend');
        ul.setAttribute('aria-label', 'Calendar key');
        if (used.market) {
            var li = el('li', 'hc-key hc-key-market');
            li.appendChild(el('span', 'hc-key-swatch'));
            li.appendChild(el('span', null, this.label('market', 'Holiday Market')));
            ul.appendChild(li);
        }
        if (used.important) {
            var li2 = el('li', 'hc-key hc-key-important');
            var s = el('span', 'hc-key-swatch');
            s.innerHTML = ICONS.star;
            li2.appendChild(s);
            li2.appendChild(el('span', null, this.label('important', 'Important Date')));
            ul.appendChild(li2);
        }
        if (used.closed) {
            var li3 = el('li', 'hc-key hc-key-closed');
            li3.appendChild(el('span', 'hc-key-swatch'));
            li3.appendChild(el('span', null, this.label('closed', 'Closed')));
            ul.appendChild(li3);
        }
        var li4 = el('li', 'hc-key hc-key-today');
        li4.appendChild(el('span', 'hc-key-swatch'));
        li4.appendChild(el('span', null, 'Today'));
        ul.appendChild(li4);
        return ul;
    };

    HolidayCalendar.prototype.renderMonth = function (y, m) {
        var self = this;
        var sec = el('section', 'hc-month');
        var titleId = this.root.id + '-m' + y + pad(m);
        sec.setAttribute('aria-labelledby', titleId);
        var h = el('h3', 'hc-month-title');
        h.id = titleId;
        h.appendChild(el('span', 'hc-month-name', MONTHS[m - 1]));
        h.appendChild(el('span', 'hc-month-year', String(y)));
        sec.appendChild(h);

        var wk = el('div', 'hc-weekdays');
        wk.setAttribute('aria-hidden', 'true');
        DOW_SHORT.forEach(function (d) {
            var c = el('span', 'hc-wd');
            c.appendChild(el('span', 'hc-wd-long', d));
            c.appendChild(el('span', 'hc-wd-short', d.charAt(0)));
            wk.appendChild(c);
        });
        sec.appendChild(wk);

        var grid = el('div', 'hc-grid');
        var first = dowOf(iso(y, m, 1));
        for (var b = 0; b < first; b++) { grid.appendChild(el('div', 'hc-cell is-blank')); }
        var n = daysIn(y, m);
        for (var d = 1; d <= n; d++) { grid.appendChild(self.renderDay(iso(y, m, d), d, n)); }
        sec.appendChild(grid);
        return sec;
    };

    HolidayCalendar.prototype.renderDay = function (dateStr, d, monthLen) {
        var self = this;
        var day = this.days[dateStr];
        var active = hasContent(day);
        var wd = dowOf(dateStr);
        var cell = el(active ? 'button' : 'div', 'hc-cell hc-day');
        cell.setAttribute('data-date', dateStr);
        var cls = [];

        if (dateStr === this.today) { cls.push('is-today'); }
        else if (dateStr < this.today) { cls.push('is-past'); }

        cell.appendChild(el('span', 'hc-num', pad(d)));

        if (!active) {
            cell.className += ' ' + cls.join(' ');
            return cell;
        }

        cell.type = 'button';
        cls.push('is-active');
        cls.push('tile-' + (TILES[day.tile] ? day.tile : 'green'));
        if (LIGHT_TILES[day.tile]) { cls.push('is-light'); }

        if (day.important) {
            cls.push('is-important');
            var star = el('span', 'hc-star');
            star.innerHTML = ICONS.star;
            cell.appendChild(star);
        }

        var text = tileText(day);
        if (text) {
            var lab = el('span', 'hc-label', text);
            var col = labelColor(day);
            if (col) { lab.style.color = col; }
            cell.appendChild(lab);
        }

        if (day.closed) {
            cls.push('is-closed');
            cell.appendChild(el('span', 'hc-closed', trim(day.closedText) || this.label('closed', 'Closed')));
        }

        // Event dots (shown on small screens where tile text is hidden)
        var evCount = events(day).length || (isListWorthy(day) ? 1 : 0);
        if (evCount) {
            var dots = el('span', 'hc-dots');
            for (var i = 0; i < Math.min(evCount, 3); i++) { dots.appendChild(el('span', 'hc-dot')); }
            cell.appendChild(dots);
        }

        if (day.market) {
            cls.push('is-market');
            var prev = d > 1 && wd > 0 ? this.days[addDays(dateStr, -1)] : null;
            var next = d < monthLen && wd < 6 ? this.days[addDays(dateStr, 1)] : null;
            var joinL = !!(prev && prev.market);
            var joinR = !!(next && next.market);
            if (joinL) { cls.push('mk-join-l'); }
            if (joinR) { cls.push('mk-join-r'); }
            var barEl = el('span', 'hc-bar');
            barEl.setAttribute('aria-hidden', 'true');
            if (!joinL && this.cfg.badgeUrl) {
                var img = el('img', 'hc-badge');
                img.src = this.cfg.badgeUrl;
                img.alt = '';
                img.loading = 'lazy';
                barEl.appendChild(img);
            }
            cell.appendChild(barEl);
        }

        cell.className += ' ' + cls.join(' ');
        cell.setAttribute('aria-label', this.ariaFor(dateStr, day));
        cell.setAttribute('aria-haspopup', 'dialog');
        cell.addEventListener('click', function () { self.open(dateStr, cell); });
        return cell;
    };

    HolidayCalendar.prototype.ariaFor = function (dateStr, day) {
        var bits = [DOW[dowOf(dateStr)] + ', ' + longDate(dateStr)];
        if (dateStr === this.today) { bits.push('today'); }
        if (day.closed) { bits.push(trim(day.closedText) || this.label('closed', 'Closed')); }
        if (day.market) {
            var hrs = fmtRange(day.marketStart, day.marketEnd);
            bits.push(this.label('market', 'Holiday Market') + (hrs ? ' ' + hrs + ' ' + TZ_LABEL : ''));
        }
        if (day.important) { bits.push(this.label('important', 'Important Date')); }
        var t = tileText(day);
        if (t) { bits.push(t); }
        return bits.join('. ') + '. View details';
    };

    // Arrow-key movement between day buttons: left/right = prev/next active day, up/down = same weekday +/- 1 week.
    HolidayCalendar.prototype.bindGridKeys = function (wrap) {
        var self = this;
        wrap.addEventListener('keydown', function (e) {
            var t = e.target;
            if (!t || !t.classList || !t.classList.contains('hc-day')) { return; }
            var keys = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
            if (!(e.key in keys)) { return; }
            var step = keys[e.key];
            var date = t.getAttribute('data-date');
            for (var i = 0; i < 62; i++) {
                date = addDays(date, Math.abs(step) === 7 ? step : (step > 0 ? 1 : -1));
                if (!self.inRange(date)) { return; }
                var target = wrap.querySelector('button.hc-day[data-date="' + date + '"]');
                if (target) { e.preventDefault(); target.focus(); return; }
                if (Math.abs(step) === 7) { return; }
            }
        });
    };

    // ---------------------------------------------------------------- list view

    HolidayCalendar.prototype.renderList = function () {
        var self = this;
        var list = this.listEl;
        list.innerHTML = '';

        var dates = Object.keys(this.days).filter(function (k) {
            return self.inRange(k) && isListWorthy(self.days[k]);
        }).sort();
        var pastCount = dates.filter(function (k) { return k < self.today; }).length;

        if (pastCount) {
            var tg = el('label', 'hc-past-toggle');
            var cb = el('input');
            cb.type = 'checkbox';
            cb.checked = this.showPast;
            cb.addEventListener('change', function () { self.showPast = cb.checked; self.renderList(); });
            tg.appendChild(cb);
            tg.appendChild(el('span', null, 'Show past dates (' + pastCount + ')'));
            list.appendChild(tg);
        }

        var shown = dates.filter(function (k) { return self.showPast || k >= self.today; });
        if (!shown.length) {
            list.appendChild(el('p', 'hc-empty', 'No upcoming dates right now - check back soon!'));
            return;
        }

        var currentMonth = '';
        var ul = null;
        shown.forEach(function (k) {
            var p = parseIso(k);
            var mk = k.slice(0, 7);
            if (mk !== currentMonth) {
                currentMonth = mk;
                list.appendChild(el('h3', 'hc-list-month', MONTHS[p.m - 1] + ' ' + p.y));
                ul = el('ul', 'hc-rows');
                list.appendChild(ul);
            }
            var day = self.days[k];
            var li = el('li');
            var btn = el('button', 'hc-row' + (k === self.today ? ' is-today' : '') + (k < self.today ? ' is-past' : ''));
            btn.type = 'button';
            btn.setAttribute('aria-haspopup', 'dialog');
            btn.setAttribute('aria-label', self.ariaFor(k, day));

            var date = el('span', 'hc-row-date');
            date.appendChild(el('span', 'hc-row-dow', DOW_SHORT[dowOf(k)]));
            date.appendChild(el('span', 'hc-row-num', String(p.d)));
            if (day.important) {
                var st = el('span', 'hc-row-star');
                st.innerHTML = ICONS.star;
                date.appendChild(st);
            }
            btn.appendChild(date);

            var body = el('span', 'hc-row-body');
            var evs = events(day);
            if (evs.length) {
                evs.forEach(function (ev) {
                    var line = el('span', 'hc-row-event');
                    line.appendChild(el('span', 'hc-row-title', trim(ev.title) || 'Event'));
                    var tm = fmtRange(ev.start, ev.end);
                    line.appendChild(el('span', 'hc-row-time', tm ? tm + ' ' + TZ_LABEL : 'Time TBA'));
                    body.appendChild(line);
                });
            } else if (tileText(day)) {
                body.appendChild(el('span', 'hc-row-title', tileText(day)));
            }
            var chips = self.chips(day, true);
            if (chips) { body.appendChild(chips); }
            btn.appendChild(body);
            btn.appendChild(icon('next', 'hc-row-go'));
            btn.addEventListener('click', function () { self.open(k, btn); });
            li.appendChild(btn);
            ul.appendChild(li);
        });
    };

    HolidayCalendar.prototype.chips = function (day, compact) {
        var wrap = el('span', 'hc-chips');
        var any = false;
        if (day.closed) {
            wrap.appendChild(el('span', 'hc-chip hc-chip-closed', trim(day.closedText) || this.label('closed', 'Closed')));
            any = true;
        }
        if (day.market) {
            var hrs = fmtRange(day.marketStart, day.marketEnd);
            wrap.appendChild(el('span', 'hc-chip hc-chip-market', this.label('market', 'Holiday Market') + (hrs ? ' \u00b7 ' + hrs + ' ' + TZ_LABEL : '')));
            any = true;
        }
        if (day.important && !compact) {
            var c = el('span', 'hc-chip hc-chip-important');
            var s = el('span', 'hc-chip-star');
            s.innerHTML = ICONS.star;
            c.appendChild(s);
            c.appendChild(el('span', null, this.label('important', 'Important Date')));
            wrap.appendChild(c);
            any = true;
        }
        return any ? wrap : null;
    };

    // ---------------------------------------------------------------- dialog

    HolidayCalendar.prototype.buildDialog = function () {
        var self = this;
        var dlg = el('dialog', 'hc-dialog');
        var headId = this.root.id + '-dlg-title';
        dlg.setAttribute('aria-labelledby', headId);

        var sheet = el('div', 'hc-sheet');
        var head = el('div', 'hc-d-head');
        var title = el('h3', 'hc-d-title');
        title.id = headId;
        this.dDow = el('span', 'hc-d-dow');
        this.dDate = el('span', 'hc-d-date');
        title.appendChild(this.dDow);
        title.appendChild(this.dDate);
        head.appendChild(title);
        var close = el('button', 'hc-d-close');
        close.type = 'button';
        close.setAttribute('aria-label', 'Close');
        close.innerHTML = ICONS.close;
        close.addEventListener('click', function () { self.close(); });
        head.appendChild(close);
        sheet.appendChild(head);

        this.dBody = el('div', 'hc-d-body');
        sheet.appendChild(this.dBody);

        var foot = el('div', 'hc-d-foot');
        this.dPrev = el('button', 'hc-d-nav hc-d-prev');
        this.dPrev.type = 'button';
        this.dNext = el('button', 'hc-d-nav hc-d-next');
        this.dNext.type = 'button';
        this.dShare = el('button', 'hc-d-share');
        this.dShare.type = 'button';
        this.dPrev.addEventListener('click', function () { self.step(-1); });
        this.dNext.addEventListener('click', function () { self.step(1); });
        this.dShare.addEventListener('click', function () { self.share(); });
        foot.appendChild(this.dPrev);
        foot.appendChild(this.dShare);
        foot.appendChild(this.dNext);
        sheet.appendChild(foot);

        dlg.appendChild(sheet);

        // Click on the backdrop (the dialog box itself, outside the sheet) closes.
        dlg.addEventListener('click', function (e) { if (e.target === dlg) { self.close(); } });
        dlg.addEventListener('close', function () { self.afterClose(); });
        dlg.addEventListener('cancel', function () { /* Esc - native close; afterClose runs on 'close' */ });
        dlg.addEventListener('keydown', function (e) {
            var tag = (e.target && e.target.tagName) || '';
            if (tag === 'INPUT' || tag === 'TEXTAREA') { return; }
            if (e.key === 'ArrowLeft') { e.preventDefault(); self.step(-1); }
            if (e.key === 'ArrowRight') { e.preventDefault(); self.step(1); }
        });

        // Swipe left/right between dates (mobile) - the client's "swipe for event details" idea.
        var sx = 0, sy = 0, tracking = false;
        sheet.addEventListener('touchstart', function (e) {
            if (e.touches.length !== 1) { tracking = false; return; }
            tracking = true;
            sx = e.touches[0].clientX;
            sy = e.touches[0].clientY;
        }, { passive: true });
        sheet.addEventListener('touchend', function (e) {
            if (!tracking) { return; }
            tracking = false;
            var t = e.changedTouches[0];
            var dx = t.clientX - sx, dy = t.clientY - sy;
            if (Math.abs(dx) > 60 && Math.abs(dy) < 45) { self.step(dx < 0 ? 1 : -1, true); }
        }, { passive: true });

        this.dialog = dlg;
        this.app.appendChild(dlg);
    };

    HolidayCalendar.prototype.open = function (dateStr, opener) {
        var day = this.days[dateStr];
        if (!day) { return; }
        this.current = dateStr;
        if (opener) { this.opener = opener; }
        this.fillDialog(dateStr, day);

        var dlg = this.dialog;
        if (!dlg.open) {
            if (typeof dlg.showModal === 'function') { dlg.showModal(); }
            else { dlg.setAttribute('open', ''); dlg.classList.add('hc-fallback'); }
            document.documentElement.classList.add('hc-lock');
        }
        this.setHash(dateStr);
    };

    HolidayCalendar.prototype.fillDialog = function (dateStr, day, direction) {
        var self = this;
        var body = this.dBody;
        var pageUrl = this.pageUrl(dateStr);
        this.dDow.textContent = DOW[dowOf(dateStr)] + (dateStr === this.today ? ' \u00b7 Today' : '');
        this.dDate.textContent = longDate(dateStr);
        body.innerHTML = '';
        body.scrollTop = 0;
        if (direction) {
            body.classList.remove('hc-slide-l', 'hc-slide-r');
            void body.offsetWidth;  // restart animation
            body.classList.add(direction > 0 ? 'hc-slide-l' : 'hc-slide-r');
        }

        var chips = this.chips(day, false);
        if (chips) { body.appendChild(chips); }

        if (trim(day.note)) { body.appendChild(el('p', 'hc-d-note', trim(day.note))); }

        var evs = events(day);
        if (evs.length) {
            var ul = el('ul', 'hc-events');
            evs.forEach(function (ev, i) { ul.appendChild(self.renderEvent(dateStr, ev, i, pageUrl)); });
            body.appendChild(ul);
        } else if (trim(day.label) && !trim(day.note)) {
            body.appendChild(el('p', 'hc-d-note', trim(day.label)));
        }

        if (!body.childNodes.length) {
            body.appendChild(el('p', 'hc-d-note', 'No scheduled events.'));
        }

        // Prev / next among dates that have something on them.
        var idx = this.order.indexOf(dateStr);
        var prev = idx > 0 ? this.order[idx - 1] : null;
        var next = idx > -1 && idx < this.order.length - 1 ? this.order[idx + 1] : null;
        this.navBtn(this.dPrev, prev, 'prev');
        this.navBtn(this.dNext, next, 'next');
        this.dShare.innerHTML = ICONS.link;
        this.dShare.appendChild(el('span', null, 'Share'));
    };

    HolidayCalendar.prototype.navBtn = function (btn, date, dir) {
        btn.innerHTML = '';
        btn.disabled = !date;
        btn.setAttribute('data-date', date || '');
        if (!date) { btn.setAttribute('aria-hidden', 'true'); btn.style.visibility = 'hidden'; return; }
        btn.removeAttribute('aria-hidden');
        btn.style.visibility = '';
        btn.setAttribute('aria-label', (dir === 'prev' ? 'Previous date: ' : 'Next date: ') + DOW[dowOf(date)] + ', ' + longDate(date));
        if (dir === 'prev') { btn.appendChild(icon('prev')); }
        btn.appendChild(el('span', null, shortDate(date)));
        if (dir === 'next') { btn.appendChild(icon('next')); }
    };

    HolidayCalendar.prototype.renderEvent = function (dateStr, ev, i, pageUrl) {
        var self = this;
        var li = el('li', 'hc-event');
        li.appendChild(el('h4', 'hc-ev-title', trim(ev.title) || 'Event'));
        if (trim(ev.subtitle)) { li.appendChild(el('p', 'hc-ev-sub', trim(ev.subtitle))); }

        var time = el('p', 'hc-ev-time');
        time.appendChild(icon('clock'));
        var range = fmtRange(ev.start, ev.end);
        time.appendChild(el('span', null, range ? range + ' ' + TZ_LABEL : 'Time to be announced'));
        li.appendChild(time);

        if (trim(ev.description)) { li.appendChild(el('p', 'hc-ev-desc', trim(ev.description))); }

        var actions = el('div', 'hc-ev-actions');
        if (ev.linkUrl) {
            var a = el('a', 'hc-ev-link');
            a.href = ev.linkUrl;
            if (/^https?:\/\//i.test(ev.linkUrl) && ev.linkUrl.indexOf(window.location.host) === -1) {
                a.target = '_blank';
                a.rel = 'noopener';
            }
            a.appendChild(el('span', null, trim(ev.linkText) || 'Learn more'));
            a.appendChild(icon('arrow'));
            actions.appendChild(a);
        }
        if (parseTime(ev.start)) {
            var g = el('a', 'hc-ev-cal');
            g.href = googleUrl(dateStr, ev, this.cfg, pageUrl);
            g.target = '_blank';
            g.rel = 'noopener';
            g.appendChild(icon('cal'));
            g.appendChild(el('span', null, 'Google Calendar'));
            actions.appendChild(g);

            var ics = el('button', 'hc-ev-cal');
            ics.type = 'button';
            ics.appendChild(icon('cal'));
            ics.appendChild(el('span', null, 'Apple / Outlook'));
            ics.addEventListener('click', function () {
                downloadFile(slug(ev.title) + '-' + dateStr + '.ics', buildIcs(dateStr, ev, i, self.cfg, pageUrl));
            });
            actions.appendChild(ics);
        }
        if (actions.childNodes.length) { li.appendChild(actions); }
        return li;
    };

    HolidayCalendar.prototype.step = function (dir, fromSwipe) {
        if (!this.current) { return; }
        var idx = this.order.indexOf(this.current);
        var target = this.order[idx + dir];
        if (!target) { return; }
        this.current = target;
        this.fillDialog(target, this.days[target], dir);
        this.setHash(target);
        if (!fromSwipe) {
            var btn = dir < 0 ? this.dPrev : this.dNext;
            if (btn.disabled) { (dir < 0 ? this.dNext : this.dPrev).focus(); }
        }
    };

    HolidayCalendar.prototype.close = function () {
        var dlg = this.dialog;
        if (typeof dlg.close === 'function' && dlg.open) { dlg.close(); }
        else { dlg.removeAttribute('open'); this.afterClose(); }
    };

    HolidayCalendar.prototype.afterClose = function () {
        document.documentElement.classList.remove('hc-lock');
        this.clearHash();
        // Return focus to the tile for the date the visitor ended on (they may have stepped).
        var target = this.app.querySelector('.hc-' + (this.view === 'list' ? 'list' : 'months') + ' [data-date="' + this.current + '"]');
        var f = (target && target.tagName === 'BUTTON') ? target : this.opener;
        this.current = null;
        if (f && f.focus) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } }
    };

    HolidayCalendar.prototype.pageUrl = function (dateStr) {
        return window.location.href.split('#')[0] + '#hc-' + dateStr;
    };

    HolidayCalendar.prototype.setHash = function (dateStr) {
        if (!window.history || !window.history.replaceState) { return; }
        try { window.history.replaceState(window.history.state, '', '#hc-' + dateStr); } catch (e) { /* ignore */ }
    };

    HolidayCalendar.prototype.clearHash = function () {
        if (!/^#hc-\d{4}-\d{2}-\d{2}$/.test(window.location.hash)) { return; }
        try { window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search); } catch (e) { /* ignore */ }
    };

    HolidayCalendar.prototype.openFromHash = function () {
        var m = /^#hc-(\d{4}-\d{2}-\d{2})$/.exec(window.location.hash || '');
        if (!m || !this.days[m[1]] || !hasContent(this.days[m[1]]) || !this.inRange(m[1])) { return; }
        var tile = this.app.querySelector('.hc-months [data-date="' + m[1] + '"]');
        if (tile && tile.scrollIntoView) { tile.scrollIntoView({ block: 'center' }); }
        this.open(m[1], tile);
    };

    HolidayCalendar.prototype.share = function () {
        var self = this;
        var url = this.pageUrl(this.current);
        var day = this.days[this.current];
        var title = (tileText(day) || this.label('market', 'Holiday Market')) + ' \u2013 ' + shortDate(this.current);
        var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
        if (coarse && navigator.share) {
            navigator.share({ title: title, url: url }).catch(function () { /* dismissed */ });
            return;
        }
        var done = function () {
            var span = self.dShare.querySelector('span');
            if (span) { span.textContent = 'Link copied'; }
            self.dShare.classList.add('is-copied');
            setTimeout(function () {
                self.dShare.classList.remove('is-copied');
                if (span) { span.textContent = 'Share'; }
            }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(done, function () { window.prompt('Copy this link:', url); });
        } else {
            window.prompt('Copy this link:', url);
        }
    };

    // ---------------------------------------------------------------- boot

    function init() {
        var nodes = document.querySelectorAll('.sp-holiday-cal[data-hc]:not([data-hc-ready])');
        for (var i = 0; i < nodes.length; i++) {
            nodes[i].setAttribute('data-hc-ready', '');
            try { new HolidayCalendar(nodes[i]); } catch (e) { if (window.console) { console.error('Holiday calendar failed to render', e); } }
        }
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); }
    else { init(); }

    // Exposed for the page editor / tests.
    window.StudioParkHolidayCalendar = { init: init, _fmtRange: fmtRange, _todayET: todayET };
})();
